import React, { useState, useEffect } from 'react';
import { Calendar, FileText, UserCheck, ChevronRight, CheckCircle, XCircle, ArrowLeft, Download, MessageSquare } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { User } from '../types';
import { getDashboard, batchReview } from '../api';
import { formatBeijingTime } from '../utils/dateFormat';
import InterviewCalendar from './InterviewCalendar';

interface Props {
  currentUser: User;
  notificationBell?: React.ReactNode;
  onNavigateCandidate?: (id: string) => void;
  onOpenCandidate?: (id: string, candidateIds?: string[]) => void;
  onNavigateToOnboarding?: () => void;
  refreshKey?: number;
  hideHeader?: boolean;
}

export default function HomePage({ currentUser, notificationBell, onNavigateCandidate, onOpenCandidate, onNavigateToOnboarding, refreshKey, hideHeader = false }: Props) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showReviewPage, setShowReviewPage] = useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(new Set());
  const [batchProcessing, setBatchProcessing] = useState(false);

  const refreshData = () => {
    setLoading(true);
    getDashboard().then(d => setData(d)).catch(() => {}).finally(() => setLoading(false));
  };

  const refreshDataSilent = () => {
    getDashboard().then(d => setData(d)).catch(() => {});
  };

  useEffect(() => { refreshData(); }, []);

  useEffect(() => {
    if (refreshKey && refreshKey > 0) refreshDataSilent();
  }, [refreshKey]);

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return '上午好';
    if (h < 18) return '下午好';
    return '晚上好';
  };

  const pendingReviews = data?.pending_reviews || [];
  const pendingEvaluations = data?.pending_evaluations || [];
  const todayCount = data?.today_interview_count || 0;
  const pendingOnboard = data?.pending_onboard || [];
  const [showEvalPage, setShowEvalPage] = useState(false);

  const toggleTask = (id: string) => {
    setSelectedTaskIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedTaskIds.size === pendingReviews.length) {
      setSelectedTaskIds(new Set());
    } else {
      setSelectedTaskIds(new Set(pendingReviews.map((r: any) => r.id)));
    }
  };

  const handleBatchAction = async (result: 'passed' | 'rejected') => {
    const ids = Array.from(selectedTaskIds);
    if (ids.length === 0) { toast.error('请先选择简历'); return; }
    const label = result === 'passed' ? '通过' : '淘汰';
    if (!window.confirm(`确认将 ${ids.length} 份简历标记为「${label}」？`)) return;
    try {
      setBatchProcessing(true);
      const res = await batchReview(ids, result);
      toast.success(res.message || `已处理 ${ids.length} 份`);
      setSelectedTaskIds(new Set());
      refreshDataSilent();
    } catch { toast.error('批量处理失败'); } finally { setBatchProcessing(false); }
  };

  // ===== Batch Review Page =====
  if (showReviewPage) {
    return (
      <div className="h-full flex flex-col bg-gray-50">
        <div className="shrink-0 bg-white border-b border-gray-200 px-4 md:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => { setShowReviewPage(false); setSelectedTaskIds(new Set()); }}
              className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
              <ArrowLeft size={18} className="text-gray-500" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 tracking-tight">待评审简历</h1>
              <p className="text-sm text-gray-400">{pendingReviews.length} 份待处理</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {selectedTaskIds.size > 0 && (
              <>
                <span className="text-xs text-gray-500">已选 {selectedTaskIds.size} 份</span>
                <button onClick={() => handleBatchAction('passed')} disabled={batchProcessing}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50 shadow-sm">
                  <CheckCircle size={12} className="inline mr-1" />批量通过
                </button>
                <button onClick={() => handleBatchAction('rejected')} disabled={batchProcessing}
                  className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50">
                  <XCircle size={12} className="inline mr-1" />批量淘汰
                </button>
              </>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Select all bar */}
          <div className="sticky top-0 bg-gray-50 px-4 md:px-6 py-2 flex items-center gap-3 border-b border-gray-100 z-10">
            <label className="flex items-center gap-2 cursor-pointer text-xs text-gray-500">
              <input type="checkbox" checked={selectedTaskIds.size === pendingReviews.length && pendingReviews.length > 0}
                onChange={toggleAll} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
              全选
            </label>
          </div>

          {pendingReviews.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
              <FileText size={40} strokeWidth={1} className="text-gray-300 mb-3" />
              <p className="text-sm">暂无待评审简历</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {pendingReviews.map((r: any) => (
                <div key={r.id} className="flex items-center gap-3 px-4 md:px-6 py-3 hover:bg-white transition-colors">
                  <input type="checkbox" checked={selectedTaskIds.has(r.id)}
                    onChange={() => toggleTask(r.id)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 shrink-0" />
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => {
                      const cIds = pendingReviews.map((t: any) => t.candidate_id);
                      onOpenCandidate?.(r.candidate_id, cIds);
                    }}>
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center text-[11px] font-bold text-primary-600 shrink-0">
                        {(r.candidate_name || '?').charAt(0)}
                      </div>
                      <span className="text-sm font-medium text-gray-800">{r.candidate_name}</span>
                      {r.position_name && (
                        <span className="text-[10px] text-primary-600 bg-primary-50 px-1.5 py-0.5 rounded-full">{r.position_name}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-[11px] text-gray-400 mt-1 ml-9">
                      {r.school && <span>{r.school}</span>}
                      {r.education && <span>· {r.education}</span>}
                      {r.latest_company && <span>· {r.latest_company}</span>}
                      {r.latest_title && <span>· {r.latest_title}</span>}
                      {r.source && <span>· {r.source}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {r.resume_file && (
                      <a href={r.resume_file} target="_blank" rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-primary-600"
                        title="查看简历">
                        <Download size={14} />
                      </a>
                    )}
                    <button onClick={(e) => { e.stopPropagation(); handleBatchAction_single(r.id, 'passed'); }}
                      className="p-1.5 rounded-lg hover:bg-primary-50 transition-colors text-gray-300 hover:text-primary-600" title="通过">
                      <CheckCircle size={16} />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); handleBatchAction_single(r.id, 'rejected'); }}
                      className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-300 hover:text-gray-600" title="淘汰">
                      <XCircle size={16} />
                    </button>
                  </div>
                  <div className="text-[10px] text-gray-400 shrink-0 w-16 text-right">
                    {r.created_by_name && <span>{r.created_by_name}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  async function handleBatchAction_single(taskId: string, result: 'passed' | 'rejected') {
    const label = result === 'passed' ? '通过' : '淘汰';
    try {
      await batchReview([taskId], result);
      toast.success(`已标记为${label}`);
      refreshDataSilent();
    } catch { toast.error('处理失败'); }
  }

  // ===== Pending Evaluations Page =====
  if (showEvalPage) {
    return (
      <div className="h-full flex flex-col bg-gray-50">
        <div className="shrink-0 bg-white border-b border-gray-200 px-4 md:px-6 py-3 flex items-center gap-3">
          <button onClick={() => setShowEvalPage(false)}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <ArrowLeft size={18} className="text-gray-500" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900 tracking-tight">待处理面试评价</h1>
            <p className="text-sm text-gray-400">{pendingEvaluations.length} 场待填写</p>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {pendingEvaluations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
              <MessageSquare size={40} strokeWidth={1} className="text-gray-300 mb-3" />
              <p className="text-sm">所有面试评价已完成</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {pendingEvaluations.map((ev: any) => (
                <div key={ev.interview_id}
                  className="flex items-center gap-3.5 px-4 md:px-6 py-3.5 hover:bg-white cursor-pointer transition-colors"
                  onClick={() => onOpenCandidate?.(ev.candidate_id)}>
                  <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center text-xs font-bold text-primary-600 shrink-0">
                    {(ev.candidate_name || '?').charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-800">{ev.candidate_name}</span>
                      <span className={clsx('text-[10px] px-1.5 py-0.5 rounded font-medium',
                        ev.round?.includes('终') ? 'bg-primary-100 text-primary-700' :
                        ev.round?.includes('二') ? 'bg-primary-50 text-primary-600' : 'bg-gray-100 text-gray-500'
                      )}>{ev.round}</span>
                      {ev.position_name && (
                        <span className="text-[10px] text-primary-600 bg-primary-50 px-1.5 py-0.5 rounded-full">{ev.position_name}</span>
                      )}
                    </div>
                    <div className="text-[11px] text-gray-400 mt-1">
                      面试时间: {(ev.scheduled_time || '').replace('T', ' ').slice(0, 16)}
                      {ev.iv_status === 'completed' ? <span className="ml-2 text-primary-600">已结束</span>
                        : <span className="ml-2 text-gray-500">已过期未填写</span>}
                    </div>
                  </div>
                  <span className="text-xs text-primary-600 bg-primary-50 px-2 py-1 rounded-lg font-medium shrink-0">去评价</span>
                  <ChevronRight size={14} className="text-gray-300 shrink-0" />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ① 待处理项数据
  const todoItems: { label: string; count: number; icon: React.ReactNode; iconBg: string; onClick?: () => void }[] = [
    {
      label: '待评审简历', count: pendingReviews.length,
      icon: <FileText size={17} className="text-primary-500" />, iconBg: 'bg-primary-50',
      onClick: pendingReviews.length > 0 ? () => setShowReviewPage(true) : undefined,
    },
    {
      label: '待处理评价', count: pendingEvaluations.length,
      icon: <MessageSquare size={17} className="text-primary-500" />, iconBg: 'bg-primary-50',
      onClick: pendingEvaluations.length > 0 ? () => setShowEvalPage(true) : undefined,
    },
    {
      label: '今日面试', count: todayCount,
      icon: <Calendar size={17} className="text-primary-500" />, iconBg: 'bg-primary-50',
    },
    {
      label: '待入职', count: pendingOnboard.length,
      icon: <UserCheck size={17} className="text-primary-500" />, iconBg: 'bg-primary-50',
      onClick: pendingOnboard.length > 0 && onNavigateToOnboarding ? onNavigateToOnboarding : undefined,
    },
  ];

  // ===== Main Dashboard =====
  return (
    <div className={clsx('h-full flex flex-col', hideHeader ? 'bg-transparent' : 'bg-gray-50')}>
      {/* Header */}
      {!hideHeader && (
        <div className="shrink-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900 tracking-tight">{greeting()}，{currentUser.display_name || currentUser.username}</h1>
            <p className="text-sm text-gray-500 mt-0.5">这是你的待办事项概览</p>
          </div>
          {notificationBell}
        </div>
      )}

      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent" />
        </div>
      ) : (
        <div className={clsx('flex-1 min-h-0 overflow-y-auto xl:overflow-hidden', hideHeader ? 'p-0' : 'p-3 md:p-4')}>
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-2.5 xl:h-full">

            {/* ================= 左列 ================= */}
            <div className="xl:col-span-2 flex flex-col gap-2.5 min-h-0">

              {/* ===== ① 待办色块（马卡龙柔和配色） ===== */}
              <div className="shrink-0 grid grid-cols-2 md:grid-cols-4 gap-2.5">
                {todoItems.map((it, idx) => {
                  const bg = [
                    'bg-[#F2E6B8]/60', // 淡奶油黄
                    'bg-[#CFE0F2]/60', // 雾霾蓝
                    'bg-[#E0D2F0]/60', // 浅灰紫
                    'bg-[#CDE8D9]/60', // 淡薄荷绿
                  ][idx % 4];
                  return (
                    <button key={it.label} onClick={it.onClick} disabled={!it.onClick}
                      className={clsx('flex flex-col items-center justify-center gap-2 rounded-2xl px-4 py-7 backdrop-blur-md ring-1 ring-inset ring-white/50 shadow-[0_4px_20px_-10px_rgba(31,45,61,0.18)] transition-transform',
                        bg,
                        it.onClick ? 'cursor-pointer hover:-translate-y-0.5' : 'cursor-default')}>
                      <span className="text-3xl font-bold text-gray-800 leading-none tabular-nums">{it.count}</span>
                      <span className="text-xs font-medium text-gray-600">{it.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* ===== ② 面试日程 ===== */}
              <div className="h-[480px] xl:h-auto xl:flex-1 min-h-0">
                <InterviewCalendar currentUserId={currentUser.id} onNavigateCandidate={onNavigateCandidate} />
              </div>
            </div>

            {/* ================= 右列 ================= */}
            <div className="flex flex-col gap-2.5 min-h-0">

              {/* ===== ③ 待评价 ===== */}
              <div className="h-[320px] xl:h-auto xl:flex-1 min-h-0 bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden">
                <div className="shrink-0 px-4 py-3 flex items-center justify-between border-b border-gray-100">
                  <div className="flex items-center gap-2 min-w-0">
                    <MessageSquare size={16} className="text-primary-500 shrink-0" />
                    <h2 className="text-sm font-bold text-gray-900">待评价</h2>
                    <span className="text-xs text-gray-500">{pendingEvaluations.length} 场</span>
                  </div>
                  {pendingEvaluations.length > 0 && (
                    <button onClick={() => setShowEvalPage(true)}
                      className="text-[11px] text-white bg-primary-600 hover:bg-primary-700 px-2.5 py-1 rounded-md font-medium flex items-center gap-0.5 shrink-0 transition-colors">
                      全部 <ChevronRight size={12} />
                    </button>
                  )}
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto">
                  {pendingEvaluations.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-gray-400">
                      <MessageSquare size={28} strokeWidth={1} className="text-gray-300 mb-2" />
                      <p className="text-xs">所有面试评价已完成</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {pendingEvaluations.map((ev: any) => (
                        <div key={ev.interview_id}
                          className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                          onClick={() => onOpenCandidate?.(ev.candidate_id)}>
                          <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center text-xs font-bold text-primary-600 shrink-0">
                            {(ev.candidate_name || '?').charAt(0)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="text-sm font-bold text-gray-900 truncate">{ev.candidate_name}</span>
                              <span className={clsx('text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0',
                                ev.round?.includes('终') ? 'bg-primary-100 text-primary-700' :
                                ev.round?.includes('二') ? 'bg-primary-50 text-primary-600' : 'bg-gray-100 text-gray-500'
                              )}>{ev.round}</span>
                            </div>
                            {ev.position_name && (
                              <div className="text-xs text-gray-500 mt-0.5 truncate">{ev.position_name}</div>
                            )}
                          </div>
                          <span className="text-[11px] text-white bg-primary-600 hover:bg-primary-700 px-2.5 py-1 rounded-md font-medium shrink-0 transition-colors whitespace-nowrap">去评价</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* ===== ④ 跟进入职 ===== */}
              <div className="h-[320px] xl:h-auto xl:flex-1 min-h-0 bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden">
                <div className="shrink-0 px-4 py-3 flex items-center justify-between border-b border-gray-100">
                  <div className="flex items-center gap-2 min-w-0">
                    <UserCheck size={16} className="text-primary-500 shrink-0" />
                    <h2 className="text-sm font-bold text-gray-900">跟进入职</h2>
                    <span className="text-xs text-gray-500">{pendingOnboard.length} 人</span>
                  </div>
                  {pendingOnboard.length > 0 && onNavigateToOnboarding && (
                    <button onClick={onNavigateToOnboarding}
                      className="text-[11px] text-white bg-primary-600 hover:bg-primary-700 px-2.5 py-1 rounded-md font-medium flex items-center gap-0.5 shrink-0 transition-colors">
                      入职流程 <ChevronRight size={12} />
                    </button>
                  )}
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto">
                  {pendingOnboard.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-gray-400">
                      <UserCheck size={28} strokeWidth={1} className="text-gray-300 mb-2" />
                      <p className="text-xs">暂无待入职候选人</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {pendingOnboard.map((c: any) => {
                        const infoParts: string[] = [];
                        if (c.school) infoParts.push(c.school);
                        if (c.education) infoParts.push(c.education);
                        if (c.latest_company) infoParts.push(c.latest_company);
                        if (c.expected_city) infoParts.push(c.expected_city);
                        return (
                          <div key={c.id}
                            onClick={() => onNavigateCandidate?.(c.id)}
                            className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                          >
                            <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center text-xs font-bold text-primary-600 shrink-0">
                              {(c.name || '?').charAt(0)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="text-sm font-bold text-gray-900 truncate">{c.name}</span>
                                {c.position_name && (
                                  <span className="text-[10px] text-primary-600 bg-primary-50 px-1.5 py-0.5 rounded-lg shrink-0 line-clamp-2 md:line-clamp-1 max-w-[140px] md:max-w-[96px]">{c.position_name}</span>
                                )}
                              </div>
                              {infoParts.length > 0 && (
                                <div className="text-xs text-gray-500 mt-0.5 line-clamp-2 md:line-clamp-1">{infoParts.join(' · ')}</div>
                              )}
                            </div>
                            <div className="text-right shrink-0">
                              {c.onboard_date ? (
                                <div className="text-xs font-semibold text-primary-700 bg-primary-50 px-2 py-0.5 rounded whitespace-nowrap">
                                  {c.onboard_date}
                                </div>
                              ) : (
                                <div className="text-xs font-semibold text-gray-600 bg-gray-100 px-2 py-0.5 rounded whitespace-nowrap">
                                  待定
                                </div>
                              )}
                              {c.offer_approved_at && (
                                <div className="text-[11px] text-gray-500 mt-0.5 whitespace-nowrap">Offer {formatBeijingTime(c.offer_approved_at, 'date')}</div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}
